<script setup>
import { computed, reactive } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { KkLoading, KkButton } from '../../../../components/base'
import { KkInfo } from '../../../../components/ui'
import StoresModal from '../../../../components/Modals/StoresModal'
import SuggestField from '../../../../components/SuggestField'
import DiffModal from '../../../../components/CompaniesLocationStoresBulkEdit/DiffModal'

const store = useStore()
const router = useRouter()
const { t } = useI18n()

const franchisees = computed(() => store.state.companiesFranchisees.data)
const franchiseesLoading = computed(() => store.state.companiesFranchisees.loading)
const diffLoading = computed(() => store.state.companiesBulkUpdateStoreOptions.loading)
const rawDifferencesData = computed(() => store.state.companiesBulkUpdateStoreOptions.data)
const areas = computed(() => store.state.companiesAreas.data)
const areasLoading = computed(() => store.state.companiesAreas.loading)
const userCompanies = computed(() => store.state.companiesUsers.data)
const userCompaniesLoading = computed(() => store.state.companiesUsers.loading)
const originalTagGroups = computed(() => store.state.companiesOriginalTagGroups.data)
const originalTagGroupsLoading = computed(() => store.state.companiesOriginalTagGroups.loading)
const stores = computed(() => store.state.companiesStores.data?.data)
const selectedStoreIds = computed(() => store.state.companiesBulkUpdateStoreOptions.storeIds)
const storeIdCount = computed(() => store.state.companiesBulkUpdateStoreOptions.storeIds.length)
const storeOptionLoading = computed(() => franchiseesLoading.value || areasLoading.value || userCompaniesLoading.value || originalTagGroupsLoading.value)
const selectedStores = computed(() => stores.value.filter(s => selectedStoreIds.value.includes(s.id)))
const bulkUpdateStoreOptions = (query) => store.dispatch('companiesBulkUpdateStoreOptions/bulkUpdateStoreOptions', query)
const readBulkUpdatesStoreOptionDifferences = (query) => store.dispatch('companiesBulkUpdateStoreOptions/readBulkUpdatesStoreOptionDifferences', query)
const readFranchisees = ({ company_uuid }) => store.dispatch('companiesFranchisees/readFranchisees',  company_uuid)
const readUsers = ({ company_uuid, except_paginate }) => store.dispatch('companiesUsers/readUsers', { company_uuid: company_uuid, except_paginate: except_paginate })
const readAreas = ({ company_uuid }) => store.dispatch('companiesAreas/readAreas', company_uuid)
const readOriginalTagGroups = ({ company_uuid }) => store.dispatch('companiesOriginalTagGroups/readOriginalTagGroups', company_uuid)
const setStoreIds = (storeIds) => store.dispatch('companiesBulkUpdateStoreOptions/setStoreIds', storeIds)
const { company_uuid, storeIds } = useRoute().params

const isPossibleSelectFranchisees = computed(() => {
  if (!franchisees.value) { return [] }

  let filterFranchisees = franchisees.value
  if (state.inputValFranchisee) {
    filterFranchisees = filterFranchisees.filter(franchisee => franchisee.attributes.name.includes(state.inputValFranchisee))
  }

  const suggestFranchisees = filterFranchisees.map(franchisee => (
    {
      'id': franchisee.id,
      'name': franchisee.attributes.name,
      'franchiseeName': franchisee.attributes.name
    }
  ))

  suggestFranchisees.unshift(
    {
      'id': null,
      'name': t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label'),
      'franchiseeName': null
    }
  )
  return suggestFranchisees
})
const isPossibleSelectUserCompanies = computed(() => {
  if (!userCompanies.value) { return [] }

  let filterUserCompanies = userCompanies.value
  if (state.inputValAreaManager) {
    filterUserCompanies = filterUserCompanies.filter(userCompany => userCompany.attributes.name.includes(state.inputValAreaManager))
  }

  const suggestUserCompanies = filterUserCompanies.map(userCompany => (
    {
      'id': userCompany.id,
      'name': userCompany.attributes.name,
      'userCompanyName': userCompany.attributes.name
    }
  ))

  suggestUserCompanies.unshift(
    {
      'id': null,
      'name': t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label'),
      'userCompanyName': null
    }
  )
  return suggestUserCompanies
})
const isPossibleSelectAreas = computed(() => {
  if (!areas.value) { return [] }

  let filterAreas = areas.value
  if (state.inputValArea) {
    filterAreas = filterAreas.filter(area => area.attributes.name.includes(state.inputValArea))
  }

  const suggestAreas = filterAreas.map(area => (
    {
      'id': area.id,
      'name': area.attributes.name,
      'areaName': area.attributes.name
    }
  ))

  suggestAreas.unshift(
    {
      'id': null,
      'name': t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label'),
      'areaName': null
    }
  )
  return suggestAreas
})
const isPossibleOriginalTags = computed(() => {
  return function (tagGroup) {
    if (tagGroup.tags.length === 0) { return [] }

    let filterTags = tagGroup.tags
    if (state.inputValOriginalTag[tagGroup.kind]) {
      filterTags = filterTags.filter(tag => tag.name.includes(state.inputValOriginalTag[tagGroup.kind]))
    }

    const suggestTags = filterTags.map(tag => (
      {
        'id': tag.id,
        'name': tag.name,
        'tagName': tag.name,
        'kind': tagGroup.kind,
      }
    ))

    suggestTags.unshift(
      {
        'id': null,
        'name': t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label'),
        'tagName': null,
        'kind': tagGroup.kind,
      }
    )
    return suggestTags
  }
})

const targetDiffRowCount = computed(() => {
  return rawDifferencesData.value?.length || 0
})

const state = reactive({
  modal: false,
  selectedStoresModal: false,
  franchiseeId: null,
  userCompanyId: null,
  areaId: null,
  originalTagIds: {
    a: null,
    b: null,
    c: null,
    d: null,
    e: null,
  },
  inputValFranchisee: '',
  inputValAreaManager: '',
  inputValArea: '',
  inputValOriginalTag: {
    a: '',
    b: '',
    c: '',
    d: '',
    e: '',
  },
  showForms: {
    franchisee: false,
    areaManager: false,
    area: false,
    originalTagA: false,
    originalTagB: false,
    originalTagC: false,
    originalTagD: false,
    originalTagE: false,
  }
})

const initData = () => {
  state.franchiseeId = null
  state.userCompanyId = null
  state.areaId = null
  state.originalTagIds.a = null
  state.originalTagIds.b = null
  state.originalTagIds.c = null
  state.originalTagIds.d = null
  state.originalTagIds.e = null
}

const toggleSetFranchisee = () => {
  state.showForms.franchisee = !state.showForms.franchisee
  if (!state.showForms.franchisee) { state.franchiseeId = null }
}

const toggleSetAreaManager = () => {
  state.showForms.areaManager = !state.showForms.areaManager
  if (!state.showForms.areaManager) { state.userCompanyId = null }
}

const toggleSetArea = () => {
  state.showForms.area = !state.showForms.area
  if (!state.showForms.area) { state.areaId = null }
}

const toggleSetOriginalTag = kind => {
  state.showForms[`originalTag${kind.toUpperCase()}`] = !state.showForms[`originalTag${kind.toUpperCase()}`]
  if (!state.showForms[`originalTag${kind.toUpperCase()}`]) { state.originalTagIds[kind] = null }
}

const selectFranchiseeSuggest = ({suggest}) => {
  state.franchiseeId = suggest.id
  state.inputValFranchisee = suggest.franchiseeName
}

const inputFranchiseeField = ({value}) => {
  state.inputValFranchisee = value
}

const selectUserCompanySuggest = ({suggest}) => {
  state.userCompanyId = suggest.id
  state.inputValAreaManager = suggest.userCompanyName
}

const inputAreaManagerField = ({value}) => {
  state.inputValAreaManager = value
}

const selectAreaSuggest = ({suggest}) => {
  state.areaId = suggest.id
  state.inputValArea = suggest.areaName
}

const inputAreaField = ({value}) => {
  state.inputValArea = value
}

const selectOriginalTagSuggest = ({suggest}) => {
  state.originalTagIds[suggest.kind] = suggest.id
  state.inputValOriginalTag[suggest.kind] = suggest.tagName
}

const inputOriginalTagField = ({value}, kind) => {
  state.inputValOriginalTag[kind] = value
}

const query = computed(() => {
  let storeOption = {}
  let tagIds = {}
  if(state.showForms.franchisee) storeOption['franchiseeId'] = state.franchiseeId
  if(state.showForms.areaManager) storeOption['userCompanyId'] = state.userCompanyId
  if(state.showForms.area) storeOption['areaId'] = state.areaId
  if(state.showForms.originalTagA) tagIds['original_tag_group_a_tag_id'] = state.originalTagIds.a
  if(state.showForms.originalTagB) tagIds['original_tag_group_b_tag_id'] = state.originalTagIds.b
  if(state.showForms.originalTagC) tagIds['original_tag_group_c_tag_id'] = state.originalTagIds.c
  if(state.showForms.originalTagD) tagIds['original_tag_group_d_tag_id'] = state.originalTagIds.d
  if(state.showForms.originalTagE) tagIds['original_tag_group_e_tag_id'] = state.originalTagIds.e

  return {
    store_option: storeOption,
    original_tag_ids: tagIds
  }
})

const differences = computed(() => {
  const differences = []
  rawDifferencesData.value.forEach(v => {
    v = v.attributes
    let tableRowObj = { type: {} }
    const franchisee = Object.entries(v.franchisee_difference)
    const area = Object.entries(v.area_difference)
    const areaManager = Object.entries(v.user_company_difference)
    const originalTagA = Object.entries(v.original_tag_group_a_tag_difference)
    const originalTagB = Object.entries(v.original_tag_group_b_tag_difference)
    const originalTagC = Object.entries(v.original_tag_group_c_tag_difference)
    const originalTagD = Object.entries(v.original_tag_group_d_tag_difference)
    const originalTagE = Object.entries(v.original_tag_group_e_tag_difference)

    if (!differences.find(diff => diff.name === v.name)) {
      tableRowObj.name = v.name
      tableRowObj.diffCount = franchisee.length + area.length + areaManager.length + originalTagA.length + originalTagB.length + originalTagC.length + originalTagD.length + originalTagE.length
    }

    tableRowObj = insertTableRowObjToDifferences(
      franchisee,
      tableRowObj,
      differences,
      formatStoreOptionText,
      'franchisee',
      t('models.franchisee.modelName'),
      { before: 'before_value', after: 'after_value' }
    )

    tableRowObj = insertTableRowObjToDifferences(
      areaManager,
      tableRowObj,
      differences,
      formatStoreOptionText,
      'areaManager',
      t('models.areaManager.modelName'),
      { before: 'before_value', after: 'after_value' }
    )

    tableRowObj = insertTableRowObjToDifferences(
      area,
      tableRowObj,
      differences,
      formatStoreOptionText,
      'area',
      t('models.area.modelName'),
      { before: 'before_value', after: 'after_value' }
    )

    tableRowObj = insertTableRowObjToDifferences(
      originalTagA,
      tableRowObj,
      differences,
      formatStoreOptionText,
      'originalTagA',
      originalTagGroups.value.find(tagGroup => tagGroup.kind === 'a')?.name,
      { before: 'before_value', after: 'after_value' }
    )

    tableRowObj = insertTableRowObjToDifferences(
      originalTagB,
      tableRowObj,
      differences,
      formatStoreOptionText,
      'originalTagB',
      originalTagGroups.value.find(tagGroup => tagGroup.kind === 'b')?.name,
      { before: 'before_value', after: 'after_value' }
    )

    tableRowObj = insertTableRowObjToDifferences(
      originalTagC,
      tableRowObj,
      differences,
      formatStoreOptionText,
      'originalTagC',
      originalTagGroups.value.find(tagGroup => tagGroup.kind === 'c')?.name,
      { before: 'before_value', after: 'after_value' }
    )

    tableRowObj = insertTableRowObjToDifferences(
      originalTagD,
      tableRowObj,
      differences,
      formatStoreOptionText,
      'originalTagD',
      originalTagGroups.value.find(tagGroup => tagGroup.kind === 'd')?.name,
      { before: 'before_value', after: 'after_value' }
    )

    insertTableRowObjToDifferences(
      originalTagE,
      tableRowObj,
      differences,
      formatStoreOptionText,
      'originalTagE',
      originalTagGroups.value.find(tagGroup => tagGroup.kind === 'e')?.name,
      { before: 'before_value', after: 'after_value' }
    )
  })
  return differences
})

const formatStoreOptionText = (text, _key) => {//eslint-disable-line
  return text
}

const insertTableRowObjToDifferences = (attributes, tableRowObj, differences, formatMethod, typeKey, typeName, valueName) => {
  tableRowObj.type.name = typeName
  tableRowObj.type.diffCount = attributes.length
  attributes.forEach(([key, value]) => {
    tableRowObj.type.key = typeKey
    tableRowObj.beforeValue = formatMethod(value[valueName.before], key)
    tableRowObj.afterValue = formatMethod(value[valueName.after], key)
    differences.push(tableRowObj)
    tableRowObj = { type: {} }
  })
  return tableRowObj
}

const onConfirm = async () => {
  readBulkUpdatesStoreOptionDifferences({ companyUuid: company_uuid, query: query.value })
  state.modal = true
}

const onSubmit = async () => {
  bulkUpdateStoreOptions({ companyUuid: company_uuid, query: query.value })
    .then(() => { router.push({ name: 'CompaniesLocationStores' }) })
}

const handleCancel = () => {
  router.push({ name: 'CompaniesLocationStores', params: { company_uuid: company_uuid } })
}

const load = async () => {
  await Promise.all([
    readFranchisees({ company_uuid: company_uuid }),
    readUsers({ company_uuid: company_uuid, except_paginate: true }),
    readAreas({ company_uuid: company_uuid }),
    readOriginalTagGroups({ company_uuid: company_uuid }),
  ])
  initData()
  setStoreIds(storeIds)
}

load()
</script>

<template>
  <div>
    <div class="p-box">
      <div class="p-box__body">
        <div class="p-box">
          <header class="p-box__header">
            <h2 class="p-box__title">{{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.header') }}</h2>
          </header>
          <div class="p-box__body">
            <div class="infoWrapper">
              <KkInfo>
                {{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.description1') }}
              </KkInfo>
            </div>
            <KkLoading v-show="storeOptionLoading" />
            <template v-if="!storeOptionLoading">
              <div class="selectedStores">
                <i18n-t keypath="pages.CompaniesLocationStoresBulkUpdateWithGmb.selectedStoresCount" tag="p">
                  <span class="selectedStores__link u-colorPrimary" @click="state.selectedStoresModal = true">{{ t('labels.storesCount', storeIdCount) }}</span>
                </i18n-t>
              </div>
              <form>
                <div class="p-box">
                  <div class="p-box__body">
                    <div class="forms">
                      <section class="p-box">
                        <header class="p-box__header sectionHeader">
                          <h3 class="p-box__title">{{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.franchisee.label') }}</h3>
                        </header>
                        <div class="p-box__body">
                          <div class="infoWrapper">
                            <KkInfo>
                              {{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.franchisee.description1') }}
                            </KkInfo>
                          </div>
                          <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <input
                              id="checkbox_franchisee"
                              :checked="false"
                              class="c-checkbox__input"
                              type="checkbox"
                              @change="toggleSetFranchisee"
                            >
                            <label class="c-checkbox__label" for="checkbox_franchisee">{{
                              t('pages.CompaniesLocationStoresBulkEditStoreOptions.franchisee.checkBoxLabel') }}</label>
                          </fieldset>
                          <div v-if="state.showForms.franchisee" class="franchisee">
                            <div class="c-grid__items">
                              <SuggestField
                                :value="state.inputValFranchisee"
                                :placeholder="t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label')"
                                :suggests="isPossibleSelectFranchisees"
                                @select-suggest="selectFranchiseeSuggest"
                                @close-suggest="() => {}"
                                @focus-field="() => {}"
                                @input-field="inputFranchiseeField"
                              />
                            </div>
                          </div>
                        </div>
                      </section>
                      <section class="p-box">
                        <header class="p-box__header sectionHeader">
                          <h3 class="p-box__title">{{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.areaManager.label') }}</h3>
                        </header>
                        <div class="p-box__body">
                          <div>
                            <div class="infoWrapper">
                              <KkInfo>
                                {{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.areaManager.description1') }}
                              </KkInfo>
                            </div>
                            <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                              <input
                                id="checkbox_area_manager"
                                :checked="false"
                                class="c-checkbox__input"
                                type="checkbox"
                                @change="toggleSetAreaManager"
                              >
                              <label class="c-checkbox__label" for="checkbox_area_manager">{{
                                t('pages.CompaniesLocationStoresBulkEditStoreOptions.areaManager.checkBoxLabel') }}</label>
                            </fieldset>
                          </div>
                          <div v-if="state.showForms.areaManager" class="areaManager">
                            <div class="c-grid__items">
                              <SuggestField
                                :value="state.inputValAreaManager"
                                :placeholder="t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label')"
                                :suggests="isPossibleSelectUserCompanies"
                                @select-suggest="selectUserCompanySuggest"
                                @close-suggest="() => {}"
                                @focus-field="() => {}"
                                @input-field="inputAreaManagerField"
                              />
                            </div>
                          </div>
                        </div>
                      </section>
                      <section class="p-box">
                        <header class="p-box__header sectionHeader">
                          <h3 class="p-box__title">{{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.area.label') }}</h3>
                        </header>
                        <div class="p-box__body">
                          <div>
                            <div class="infoWrapper">
                              <KkInfo>
                                {{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.area.description1') }}
                              </KkInfo>
                            </div>
                            <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                              <input
                                id="checkbox_area"
                                :checked="false"
                                class="c-checkbox__input"
                                type="checkbox"
                                @change="toggleSetArea"
                              >
                              <label class="c-checkbox__label" for="checkbox_area">{{
                                t('pages.CompaniesLocationStoresBulkEditStoreOptions.area.checkBoxLabel') }}</label>
                            </fieldset>
                          </div>
                          <div v-if="state.showForms.area" class="area">
                            <div class="c-grid__items">
                              <SuggestField
                                :value="state.inputValArea"
                                :placeholder="t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label')"
                                :suggests="isPossibleSelectAreas"
                                @select-suggest="selectAreaSuggest"
                                @close-suggest="() => {}"
                                @focus-field="() => {}"
                                @input-field="inputAreaField"
                              />
                            </div>
                          </div>
                        </div>
                      </section>
                      <template v-for="tagGroup in originalTagGroups" :key="tagGroup.kind">
                        <section class="p-box">
                          <header class="p-box__header sectionHeader">
                            <h3 class="p-box__title">{{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.originalTagGroup.label', { tagGroupName: tagGroup.name }) }}</h3>
                          </header>
                          <div class="p-box__body">
                            <div>
                              <div class="infoWrapper">
                                <KkInfo>
                                  {{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.originalTagGroup.description1', { tagGroupName: tagGroup.name }) }}
                                </KkInfo>
                              </div>
                              <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                                <input
                                  :id="`checkbox_tag_group_${tagGroup.kind}`"
                                  :checked="false"
                                  class="c-checkbox__input"
                                  type="checkbox"
                                  @change="toggleSetOriginalTag(tagGroup.kind)"
                                >
                                <label class="c-checkbox__label" :for="`checkbox_tag_group_${tagGroup.kind}`">
                                  {{ t('pages.CompaniesLocationStoresBulkEditStoreOptions.originalTagGroup.checkBoxLabel', { tagGroupName: tagGroup.name }) }}
                                </label>
                              </fieldset>
                            </div>
                            <div v-if="state.showForms[`originalTag${tagGroup.kind.toUpperCase()}`]" class="originalTag">
                              <div class="c-grid__items">
                                <SuggestField
                                  :value="state.inputValOriginalTag[tagGroup.kind]"
                                  :placeholder="t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label')"
                                  :suggests="isPossibleOriginalTags(tagGroup)"
                                  @select-suggest="selectOriginalTagSuggest"
                                  @close-suggest="() => {}"
                                  @focus-field="() => {}"
                                  @input-field="inputOriginalTagField($event, tagGroup.kind)"
                                />
                              </div>
                            </div>
                          </div>
                        </section>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="p-box__footer">
                  <div class="c-button c-button--right">
                    <div class="buttonWrapper">
                      <KkButton :text="t('labels.cancel')"
                                color="secondary"
                                @click="handleCancel"
                      />
                    </div>
                    <div class="buttonWrapper">
                      <KkButton
                        :text="t('labels.submitFilterCond')"
                        @click="onConfirm"
                      />
                    </div>
                  </div>
                </div>
              </form>
            </template>
          </div>
        </div>
      </div>
    </div>
    <DiffModal v-if="state.modal"
               :loading="diffLoading"
               :differences="differences"
               :target-store-amount="targetDiffRowCount"
               @close="state.modal = false"
               @submit="onSubmit"
    />
    <StoresModal
      v-if="state.selectedStoresModal"
      :header-label="t('pages.CompaniesLocationStoresBulkUpdateWithGmb.selectedStores')"
      :store-names="selectedStores.map(s => s.attributes.name)"
      @close="state.selectedStoresModal = false"
    />
  </div>
</template>

<style lang="scss" scoped>
.p-modalLikeBox {
  &__header__text {
    color: #FFFFFF;
    background: rgba(58, 59, 63, 1);
  }
}
.forms {
  margin-top: 24px;
}
.p-modalLikeBox:not(:last-of-type) {
  margin-bottom: 32px;
}
.franchisee {
  margin: 32px;
}
.area {
  margin: 32px;
}
.areaManager {
  margin: 32px;
}
.originalTag {
  margin: 32px;
}
.selectedStores {
  margin-bottom: 8px;
  &__link {
    text-decoration: none;
    cursor: pointer;
  }
}
.infoWrapper {
  margin-bottom: 24px;
}
.sectionHeader {
  border: none;
}
.p-formGrid__item {
  padding: 0 8px;
}
.c-select {
  select {
    padding: 12px 48px 12px 12px;
  }
}
.buttonWrapper:not(:first-child) {
  margin-left: 8px;
}
</style>
